import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLanguageSwitcher from "../../hooks/useLanguageSwitcher.js";
import useHasAppSession from "../../hooks/useHasAppSession.js";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";
import { trackCTA, trackLead } from "../../lib/analytics";
import {
  EXPERIENCE_MENU,
  homeHrefFor,
  rendersHomeSections,
} from "../../lib/navScope.js";

const Navbar = () => {
  const location = useLocation();
  const { t } = useTranslation();
  // Language is a URL, not a piece of local state — the hook maps this page to
  // its counterpart and falls back to switching in place when it has none.
  const { language, switchTo } = useLanguageSwitcher();
  const hasAppSession = useHasAppSession();
  const analyticsContext = useAnalyticsContext();

  // Internal links resolve to the language of the page they are rendered on;
  // see `useLocalizedHref` for why they fall back to English rather than
  // prefixing paths that have no localized page.
  const localize = useLocalizedHref();
  const homeHref = localize(homeHrefFor());
  const [experienceOpen, setExperienceOpen] = useState(false);
  const experienceRef = useRef(null);

  // The dropdown closes on Escape and on a click outside it. Registered only
  // while it is open, so a closed menu costs nothing.
  useEffect(() => {
    if (!experienceOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setExperienceOpen(false);
    };
    const onPointerDown = (e) => {
      if (!experienceRef.current?.contains(e.target)) setExperienceOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [experienceOpen]);

  const appBaseUrl = import.meta.env.VITE_APP_URL;
  // Signed-in browsers are sent to their dashboard; everyone else to registration.
  // The app's own guards re-route a stale/expired session to login, so a wrong
  // guess here degrades gracefully.
  const ctaHref = hasAppSession
    ? `${appBaseUrl}/couple/dashboard`
    : `${appBaseUrl}/couple/create-event`;
  const ctaLabel = hasAppSession ? t("nav.cta.signIn") : t("nav.cta.register");

  // One hook per destination: the "navigate to Home first, then scroll" logic
  // lives in useScrollToHomeSection, which Footer also uses. This component
  // carried its own copy of it, complete with the 100ms timer, until the shell
  // rework replaced that race with router state.
  const scrollToStory = useScrollToHomeSection("how-it-works");
  const scrollToPricing = useScrollToHomeSection("pricing");

  return (
    <div
      data-collapse="small"
      data-animation="default"
      data-duration="600"
      data-easing="ease-out-cubic"
      data-easing2="ease-out"
      data-w-id="399ef015-ab98-dfe1-52e9-6d0c683a93fa"
      role="banner"
      className="navbar w-nav"
    >
      <div className="brand-wrap">
        <a
          href={homeHref}
          onClick={(e) => {
            if (rendersHomeSections(location.pathname)) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          aria-current="page"
          className="brand w-nav-brand w--current"
        >
          {/* width/height are the SVG's own viewBox dimensions. They are not
              decoration: without them the element has no aspect ratio until
              the file arrives, and agapae.css sizes it with a circular
              `height: 100%` (see .nav-logo in index.css). On a first load in
              Safari that resolved to zero and the logo never appeared. */}
          <img
            src="/assets/logo/no-symbol-logo/svg/Black logo - no background.svg"
            alt={t("nav.logo.alt")}
            width="3200"
            height="1232"
            className="nav-logo"
          />
        </a>
      </div>
      <div className="nav-container">
        <nav role="navigation" className="nav-menu w-nav-menu">
          <div className="side-nav panier"></div>
          <div className="main-nav-wrap">
            {/* THE EXPERIENCE — the primary navigation, on every page.
                One product dropdown, not a link to a section: the homepage
                defines Agapaé in prose and the six product areas are where a
                visitor goes next, so the header names them. It is a real
                <button> with aria-expanded rather than a hover-only menu,
                because a hover menu is unreachable by keyboard and unusable on
                a phone — where this nav collapses into the Webflow burger and
                the items simply stack.

                It replaced an anchor to `#features`, which only worked on the
                homepage anyway: from a product page that link navigated home
                and scrolled, which is not what "The Experience" should do. */}
            <div
              ref={experienceRef}
              className="hv2-nav-dropdown"
              /* Hover opens the menu for a pointer that can actually hover —
                 and only for one. A touch tap fires a compatibility
                 `mouseenter` before its `click`, so with `onMouseEnter` here
                 the first tap opened the menu and the `click` a few
                 milliseconds later toggled it straight back shut: the item
                 read as dead. The second tap then appeared to work only
                 because no `mouseenter` fires the second time — the browser
                 still considers the element hovered — leaving the toggle to
                 run alone.

                 Guarding on `pointerType` rather than a `(hover: hover)`
                 media query is what keeps a hybrid laptop correct: its mouse
                 still opens the menu on hover, its touchscreen still opens it
                 on the first tap. A browser without pointer events degrades to
                 click-only, which is the mobile behaviour anyway. */
              onPointerEnter={(e) => {
                if (e.pointerType === "mouse") setExperienceOpen(true);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") setExperienceOpen(false);
              }}
            >
              <button
                type="button"
                id="hv2-experience-trigger"
                className="nav-link w-inline-block hv2-nav-trigger"
                aria-expanded={experienceOpen}
                aria-haspopup="true"
                aria-controls="hv2-experience-menu"
                onClick={() => setExperienceOpen((open) => !open)}
              >
                <div className="hv2-nav-trigger-label">
                  {t("nav.links.experience")}
                  {/* The caret is the header's only signal that this item
                      opens a menu instead of going somewhere. It is driven off
                      `aria-expanded` in CSS rather than a second copy of the
                      state, so what it shows and what it announces to a screen
                      reader cannot drift apart. */}
                  <svg
                    className="hv2-nav-caret"
                    viewBox="0 0 10 6"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M1 1l4 4 4-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ball"></div>
              </button>
              {experienceOpen && (
                <ul
                  id="hv2-experience-menu"
                  className="hv2-nav-menu"
                  aria-labelledby="hv2-experience-trigger"
                >
                  {EXPERIENCE_MENU.map(({ key, href }) => (
                    <li key={key}>
                      <a href={localize(href)} className="hv2-nav-menu-link">
                        <span className="hv2-nav-menu-label">
                          {t(`nav.experienceMenu.${key}`)}
                        </span>
                        {/* One line per area. "Seating" alone tells a visitor
                            the page exists; it does not tell them what is
                            behind it, which is the whole reason the header
                            names the product areas at all. */}
                        <span className="hv2-nav-menu-note">
                          {t(`nav.experienceMenuNote.${key}`)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <a
              href="#how-it-works"
              onClick={scrollToStory}
              className="nav-link w-inline-block"
            >
              <div>{t("nav.links.story")}</div>
              <div className="ball"></div>
            </a>
            <a
              href="#pricing"
              onClick={scrollToPricing}
              className="nav-link w-inline-block"
            >
              <div>{t("nav.links.pricing")}</div>
              <div className="ball"></div>
            </a>
            <a href={localize("/contact")} className="nav-link w-inline-block">
              <div>{t("nav.links.contact")}</div>
              <div className="ball"></div>
            </a>
            {/* Live-demo nav link — TEMPORARILY HIDDEN. The Heirloom-plan demo
                CTA (Pricing card) stays the single demo entry point for now.
                Un-comment this block to restore the navbar "Live demo" link. */}
            {/*
            <a
              href={`${appBaseUrl}/try?lang=${language}`}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="nav-link w-inline-block"
            >
              <div>{t("nav.links.tryDemo")}</div>
              <div className="ball"></div>
            </a>
            */}
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link w-inline-block"
              onClick={() => {
                // A signed-in visitor is heading to their dashboard (sign in);
                // everyone else is registering.
                const ctaName = hasAppSession ? "sign_in" : "register";
                trackCTA("navbar", { ...analyticsContext, ctaName });
                // Only a real, unauthenticated visitor clicking through to
                // create-event is a lead — a signed-in visitor going to
                // their dashboard isn't a new signup signal.
                if (!hasAppSession) {
                  trackLead("navbar", { ...analyticsContext, ctaName });
                }
              }}
            >
              <div>{ctaLabel}</div>
              <div className="ball"></div>
            </a>
          </div>
          <div className="side-nav langue">
            <button
              type="button"
              onClick={() => switchTo(language === "en" ? "el" : "en")}
              className="nav-link side w-inline-block"
              aria-label={t("nav.language.toggleAriaLabel")}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              {/* Shows the language to switch TO, as a short code (EN/GR).
                  Globe is mobile-only (revealed via CSS in the mobile menu). */}
              <div className="lang-switch">
                <span className="lang-globe" aria-hidden="true">
                  🌐
                </span>
                {language === "en"
                  ? t("nav.language.el")
                  : t("nav.language.en")}
              </div>
            </button>
          </div>
        </nav>
        <div className="menu-button w-nav-button">
          <div className="line _111"></div>
          <div className="line _222"></div>
          <div className="line _333"></div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
